/**
 * Controlled search input that notifies the parent of the trimmed value after a
 * debounce delay. Clear is propagated immediately via onDebouncedChange("").
 * Use for server-side search: keep value/onChange in the parent and pass
 * onDebouncedChange to trigger the fetch.
 */

import { Input } from "@chakra-ui/react-v3";
import type { InputProps } from "@chakra-ui/react-v3";
import { useEffect, useRef } from "react";

const defaultDebounceMs = 300;

export type DebouncedSearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  onDebouncedChange?: (trimmed: string) => void;
  debounceMs?: number;
} & Omit<InputProps, "value" | "onChange">;

export function DebouncedSearchInput({
  value,
  onChange,
  onDebouncedChange,
  debounceMs = defaultDebounceMs,
  ...inputProps
}: DebouncedSearchInputProps) {
  const onDebouncedChangeRef = useRef(onDebouncedChange);
  onDebouncedChangeRef.current = onDebouncedChange;
  const effectiveDebounceMs = Math.max(1, debounceMs);

  useEffect(() => {
    if (!onDebouncedChangeRef.current) return;
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      onDebouncedChangeRef.current("");
      return;
    }
    const timer = setTimeout(() => {
      onDebouncedChangeRef.current?.(trimmed);
    }, effectiveDebounceMs);
    return () => clearTimeout(timer);
  }, [value, effectiveDebounceMs]);

  return (
    <Input
      value={value}
      onChange={e => onChange(e.target.value)}
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.emphasized"
      paddingX={2}
      paddingY={2}
      {...inputProps}
    />
  );
}
